import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useSessionContext } from '@/contexts/chat/SessionContext'
import { useStorage } from '@/contexts/StorageContext'
import { useWeather } from '@/contexts/WeatherContext'
import { useWalletContext } from '@/contexts/WalletContext'
import { useGuestSession } from '@/hooks/chat/useGuestSession'
import type { ProviderType } from '@/lib/ai-providers'
import { api, fetchWithAuth } from '@/lib/api/client'
import { authenticate } from '@/lib/auth'
import { clearGuestSession } from '@/lib/guestStorage'
import type { ChatMessageWithTimestamp } from '@/types/chat/ChatMessage'
import type { AuthCredentials, AuthStep } from '@/types/GuestSession'
import { getWebSearchEnabled, saveWebSearchEnabled } from '@/types/UserSettings'
import ChatHeader from './ChatHeader'
import InChatAuthFlow from './InChatAuthFlow'
import MessageInput from './MessageInput'
import MessagesDisplay from './MessagesDisplay'
import { useUser } from '@/hooks/useUser'
import { ModelMessage, stepCountIs, streamText } from 'ai'
import { AnthropicProviderOptions } from '@ai-sdk/anthropic'
import { systemPrompt } from '@/lib/prompt'
import { getModel } from '@/lib/ai-providers'
import { ASSISTANT_ID } from '@shared/constants/honcho'
import { createArtifactTool, createIdentityTool, createThemeTool, createMarketplaceTools } from '@/lib/ai-tools'
import { getSelectedIdentity } from '@/types/UserSettings'

const API_URL = import.meta.env.VITE_API_URL!

/**
 * Get user-friendly description for a tool invocation
 */
function getToolDescription(toolName: string, input: any): string {
  switch (toolName) {
    case 'search':
      return `Searching the web for: "${input.query}"`
    case 'remember':
      return `Remembering`
    case 'create_artifact':
      return `Creating artifact: ${input.name}`
    case 'update_artifact':
      return `Updating artifact: ${input.name || input.id}`
    case 'list_artifacts':
      return 'Listing all artifacts'
    case 'read_artifact':
      return `Reading artifact: ${input.id}`
    case 'create_theme':
      return `Creating theme: ${input.name}`
    case 'update_theme':
      return `Updating theme: ${input.id}`
    case 'list_themes':
      return 'Listing all themes'
    case 'read_theme':
      return `Reading theme: ${input.id}`
    case 'create_identity':
      return `Creating identity: ${input.name}`
    case 'update_identity':
      return `Updating identity: ${input.id}`
    case 'list_identities':
      return 'Listing all identities'
    case 'read_identity':
      return `Reading identity: ${input.id}`
    case 'search_marketplace':
      return `Searching marketplace for: "${input.query}"`
    case 'get_top_marketplace_items':
      return 'Getting top marketplace items'
    case 'purchase_marketplace_item':
      return 'Purchasing item from marketplace'
    case 'list_item_for_sale':
      return `Listing ${input.item_type}: "${input.title}"`
    case 'unlist_item':
      return 'Removing item from marketplace'
    default:
      return `Using tool: ${toolName}`
  }
}

/**
 * Get user-friendly result indicator for a tool output and trigger side effects
 */
function handleToolResult(
  toolName: string,
  output: any,
  callbacks: {
    fetchArtifacts?: () => void
    fetchThemes?: () => void
    fetchIdentities?: () => void
  }
): string {
  // Check for errors first
  if (output.error) {
    return `*Error: ${output.error}*\n`
  }

  // Trigger side effects based on tool name
  switch (toolName) {
    case 'create_artifact':
    case 'update_artifact':
      callbacks.fetchArtifacts?.()
      break
    case 'create_theme':
    case 'update_theme':
    case 'delete_theme':
      callbacks.fetchThemes?.()
      break
    case 'create_identity':
    case 'update_identity':
    case 'delete_identity':
      callbacks.fetchIdentities?.()
      break
    case 'purchase_marketplace_item':
      if (output.success) {
        if (output.item_type === 'artifact') {
          callbacks.fetchArtifacts?.()
        } else if (output.item_type === 'theme') {
          callbacks.fetchThemes?.()
        } else if (output.item_type === 'identity') {
          callbacks.fetchIdentities?.()
        }
      }
      break
    case 'list_item_for_sale':
    case 'unlist_item':
      if (output.item_type === 'artifact') {
        callbacks.fetchArtifacts?.()
      } else if (output.item_type === 'theme') {
        callbacks.fetchThemes?.()
      } else if (output.item_type === 'identity') {
        callbacks.fetchIdentities?.()
      }
      break
  }

  // Return result indicator
  switch (toolName) {
    case 'search':
      return `*Found ${output.results?.length || 0} results*\n`
    case 'create_artifact':
      return `*Created: ${output.name}*\n`
    case 'update_artifact':
      return `*Updated: ${output.name}*\n`
    case 'list_artifacts':
      return `*Found ${output.artifacts?.length || 0} artifacts*\n`
    case 'read_artifact':
      return `*Read artifact: ${output.name}*\n`
    case 'create_theme':
      return `*Created theme: ${output.name}*\n`
    case 'update_theme':
      return `*Updated theme: ${output.name}*\n`
    case 'delete_theme':
      return `*Deleted theme*\n`
    case 'list_themes':
      return `*Found ${output.themes?.length || 0} themes*\n`
    case 'read_theme':
      return `*Read theme: ${output.name}*\n`
    case 'create_identity':
      return `*Created identity: ${output.name}*\n`
    case 'update_identity':
      return `*Updated identity: ${output.name}*\n`
    case 'delete_identity':
      return `*Deleted identity*\n`
    case 'list_identities':
      return `*Found ${output.identities?.length || 0} identities*\n`
    case 'read_identity':
      return `*Read identity: ${output.name}*\n`
    case 'search_marketplace':
      return `*Found ${output.listings?.length || 0} marketplace items*\n`
    case 'get_top_marketplace_items':
      return `*Found ${output.listings?.length || 0} popular items*\n`
    case 'purchase_marketplace_item':
      return output.success ? `*Purchase completed!*\n` : `*Purchase failed*\n`
    case 'list_item_for_sale':
      return `*Listed for $${output.price.toFixed(2)}*\n`
    case 'unlist_item':
      return `*Unlisted item from marketplace*\n`
    default:
      return ''
  }
}

interface ChatInterfaceProps {
  selectedProvider: ProviderType | null
  selectedModel: string | null
  availableModels: any[]
  isLoadingModels: boolean
  modelError: string | null
  availableProviders: ProviderType[]
  onProviderChange: (provider: ProviderType) => void
  onModelChange: (modelId: string) => void
  fontMode?: 'departure-mono' | 'open-dyslexic' | 'system'
  onWebSearchStateChange?: (state: boolean, toggleHandler: () => void) => void
  onTextSizeStateChange?: (size: number, handler: (size: number) => void) => void
  onWalletStateChange?: (hasWallet: boolean) => void
  onGuestModeStateChange?: (isGuestMode: boolean) => void
  onUsageLimitReached?: (resetTime: string) => void
  onOpenSubscription?: () => void
}

export function ChatInterface({
  selectedProvider,
  selectedModel,
  availableModels,
  isLoadingModels,
  modelError,
  availableProviders,
  onProviderChange,
  onModelChange,
  fontMode = 'departure-mono',
  onWebSearchStateChange,
  onTextSizeStateChange,
  onWalletStateChange,
  onGuestModeStateChange,
  onUsageLimitReached,
  onOpenSubscription, // Used for upgrade link in rate limit messages
}: ChatInterfaceProps) {
  const {
    currentSession,
    isLoadingMessages,
    hasMoreMessages,
    isLoadingMoreMessages,
    hasMorePreviousMessages,
    isLoadingPreviousMessages,
    isGuestMode,
    saveMessages,
    renderMessages,
    updateCurrentMessage,
    loadMoreMessages,
    loadPreviousMessages,
    migrateGuestSession,
  } = useSessionContext()

  const { weather, location: userLocation } = useWeather()
  const { hasWallet } = useWalletContext()
  const { fetchArtifacts, fetchThemes, fetchIdentities, identities, isLoadingIdentities } = useStorage()
  const { user } = useUser()

  // Track selected identity for display
  const [selectedIdentityName, setSelectedIdentityName] = useState<string>(() => {
    const selectedId = getSelectedIdentity()
    return selectedId === 'default' ? import.meta.env.VITE_ASSISTANT_NAME! : selectedId
  })

  // Guest session management
  const {
    addGuestMessage,
    canSendMessage: canSendGuestMessage,
    getGuestMessages,
  } = useGuestSession()

  // Initial value for MessageInput (set from URL ?q= param, cleared after use)
  const [initialInputValue, setInitialInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [useWebSearch, setUseWebSearch] = useState(false)
  const [targetMessageId, setTargetMessageId] = useState<string | undefined>(
    undefined
  )
  const [replyingTo, setReplyingTo] = useState<ChatMessageWithTimestamp | null>(
    null
  )
  const [textSize, setTextSize] = useState<number>(() => {
    // Load from localStorage or default to 14
    const saved = localStorage.getItem('chat-text-size')
    return saved ? Number.parseInt(saved, 10) : 14
  })

  // Auth flow state for guest users
  const [showAuthFlow, setShowAuthFlow] = useState(false)
  const [authStep, setAuthStep] = useState<AuthStep | null>(null)
  const [authEmail, setAuthEmail] = useState('')
  const [authPassword, setAuthPassword] = useState('')

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const routerLocation = useLocation()

  // Initialize web search state from user settings
  useEffect(() => {
    setUseWebSearch(getWebSearchEnabled())
  }, [])

  // Save text size to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('chat-text-size', textSize.toString())
  }, [textSize])

  // Listen for identity changes (same-window)
  useEffect(() => {
    const handleIdentityChange = () => {
      const selectedId = getSelectedIdentity()
      setSelectedIdentityName(selectedId.replace(/\.json$/i, ''))
    }

    window.addEventListener('identity-changed', handleIdentityChange)
    return () => {
      window.removeEventListener('identity-changed', handleIdentityChange)
    }
  }, [identities])

  // Listen for tutorial loading state changes
  useEffect(() => {
    const handleTutorialLoading = (event: Event) => {
      const customEvent = event as CustomEvent<{ loading: boolean }>
      setIsLoading(customEvent.detail.loading)
    }

    window.addEventListener('tutorial-set-loading', handleTutorialLoading as EventListener)
    return () => {
      window.removeEventListener('tutorial-set-loading', handleTutorialLoading as EventListener)
    }
  }, [])

  // Handle all URL query params in a single effect to avoid race conditions
  useEffect(() => {
    const sessionParam = searchParams.get('session')
    const messageParam = searchParams.get('message')
    const qParam = searchParams.get('q') || searchParams.get('prompt')
    const hints = searchParams.get('hints')

    let shouldNavigate = false
    const newParams = new URLSearchParams(searchParams)

    // Handle session/message params for navigation to specific messages
    if (sessionParam && messageParam) {
      // Set target message ID immediately for highlighting
      setTargetMessageId(messageParam)

      // Switch to the target session if not already there
      // Pass messageParam to load full history (not just truncated context)
      if (currentSession.id !== sessionParam) {
        console.error('Session mismatch, cannot switch to target session')
      }

      newParams.delete('session')
      newParams.delete('message')
      shouldNavigate = true
    }

    // Handle q/prompt/hints params for pre-filling chat input
    if (qParam) {
      setInitialInputValue(qParam)

      // Auto-enable web search if hints=search (authenticated users only)
      if (hints === 'search' && !isGuestMode) {
        setUseWebSearch(true)
        saveWebSearchEnabled(true)
      }

      newParams.delete('q')
      newParams.delete('prompt')
      newParams.delete('hints')
      shouldNavigate = true
    }

    // Clear consumed params with a single navigate call
    if (shouldNavigate) {
      const queryString = newParams.toString()
      navigate(
        queryString ? `${routerLocation.pathname}?${queryString}` : routerLocation.pathname,
        { replace: true }
      )
    }
  }, [searchParams, currentSession.id, navigate, routerLocation, isGuestMode])

  // Toggle web search and save to settings
  const handleWebSearchToggle = useCallback(() => {
    if (isGuestMode) {
      return
    }
    const newState = !useWebSearch
    setUseWebSearch(newState)
    saveWebSearchEnabled(newState)
  }, [useWebSearch, isGuestMode])

  // Notify parent component of web search state changes
  useEffect(() => {
    if (onWebSearchStateChange) {
      onWebSearchStateChange(useWebSearch, handleWebSearchToggle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useWebSearch, handleWebSearchToggle, onWebSearchStateChange])

  // Notify parent component of text size state changes
  useEffect(() => {
    if (onTextSizeStateChange) {
      onTextSizeStateChange(textSize, setTextSize)
    }
  }, [textSize, setTextSize, onTextSizeStateChange])

  // Notify parent component of wallet state changes
  useEffect(() => {
    if (onWalletStateChange) {
      onWalletStateChange(hasWallet)
    }
  }, [hasWallet, onWalletStateChange])

  // Notify parent component of guest mode state changes
  useEffect(() => {
    if (onGuestModeStateChange) {
      onGuestModeStateChange(isGuestMode)
    }
  }, [isGuestMode, onGuestModeStateChange])

  const handleReplyToMessage = useCallback(
    (message: ChatMessageWithTimestamp) => {
      setReplyingTo(message)
      // Optional: Auto-focus input could be added here if needed
    },
    []
  )

  const handleClearReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  const handleDisplayMessage = useCallback(
    (message: ChatMessageWithTimestamp) => {
      renderMessages([message])
    },
    [renderMessages]
  )

  const handleSubmit = useCallback(
    async (inputValue: string) => {
      if (!inputValue.trim()) {
        return
      }

      // Prevent submission if session is still loading or ID is empty
      if (isLoadingMessages || !currentSession.id) {
        console.warn('Session not ready yet, please wait...')
        return
      }

      // Prevent submission if identities are still loading for non-default sessions
      // This ensures we can determine the correct identityId to send to the backend
      if (!isGuestMode && currentSession.id !== 'default' && isLoadingIdentities) {
        console.warn('Identities still loading, please wait...')
        return
      }

      // Clear initial value after first submit (if it was set from URL param)
      if (initialInputValue) {
        setInitialInputValue('')
      }

      let content = inputValue.trim()
      // Wrap large content in document tags for Honcho
      if (content.length > 500) {
        content = `<USER_PASTED_DOCUMENT>\n${content}\n</USER_PASTED_DOCUMENT>`
      }

      const userMessage: ChatMessageWithTimestamp = {
        role: 'user',
        content,
        timestamp: new Date(),
        // Add reply metadata if replying
        ...(replyingTo?.id && {
          replyTo: {
            messageId: replyingTo.id,
            role: replyingTo.role as 'user' | 'assistant' | 'command', // Tool messages are filtered out, so this is safe
            content: replyingTo.content,
          },
        }),
      }

      // Clear reply state after submission
      setReplyingTo(null)

      // render user message immediately
      await renderMessages([userMessage])

      if (selectedProvider && selectedModel) {
        await submitToFrontendProvider(userMessage)
        return
      }

      // In guest mode, also add to localStorage
      if (isGuestMode) {
        addGuestMessage(userMessage)
      }

      // Check guest mode message limit
      if (isGuestMode) {
        if (!canSendGuestMessage()) {
          // Trigger auth flow, don't submit message
          if (!showAuthFlow) {
            setShowAuthFlow(true)
            setAuthStep('email')

            // Assistant prompts for signup (not persisted to localStorage)
            const authPromptMessage: ChatMessageWithTimestamp = {
              role: 'assistant',
              content:
                "To keep chatting, I'll need you to create an account. It'll just take a moment.",
              timestamp: new Date(),
            }
            await renderMessages([authPromptMessage])
          }
          return // Block submission
        }
      }

      await submitToBackendProvider(userMessage)
    },
    [
      isLoadingMessages,
      currentSession.id,
      replyingTo,
      renderMessages,
      addGuestMessage,
      canSendGuestMessage,
      isGuestMode,
      showAuthFlow,
      isLoadingIdentities,
      initialInputValue,
    ]
  )

  const submitToFrontendProvider = async (userMessage: ChatMessageWithTimestamp) => {
    if (!selectedProvider || !selectedModel) {
      console.error('No provider or model selected')
      return
    }
    setIsLoading(true)
    // NOTE: we render and save different versions of the assistant message.
    // if we save the assistant message with tool call indicators, it will confuse the model.
    // so we render the assistant message with tool call indicators, but save the assistant message without tool call indicators.
    const timestamp = new Date()
    const assistantMessage: ChatMessageWithTimestamp = { role: 'assistant', content: '', timestamp }
    const assistantMessageToDisplay: ChatMessageWithTimestamp = { role: 'assistant', content: '', timestamp }

    // Track tool invocations for structured storage
    const toolInvocations: Array<{
      toolCallId: string
      toolName: string
      input: unknown
      output?: unknown
      timestamp: string
    }> = []

    // Add weather and location data if available
    // make sure to get local time based on the user's timezone
    const localTime = new Date().toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    // stream from the frontend provider
    const { data: contextData, error } = await api.api.chat['get-context'].post({
        sessionId: currentSession.id,
        targeting: {
          lastUserMessage: userMessage.content
        }
      })

    if (error) {
      console.error('Error fetching context:', error)
      setIsLoading(false)
      return
    }

    const context = contextData.context
    const prompt = systemPrompt
    const model = getModel(selectedProvider, selectedModel)
    const messages = [...context.messages.map((msg: any) => (
      { role: msg.peer_id === ASSISTANT_ID ? 'assistant' : 'user', content: msg.content }
    )), userMessage] as ModelMessage[]

    const artifactTool = createArtifactTool(currentSession.id)
    const themeTool = createThemeTool(currentSession.id)
    const identityTool = createIdentityTool(currentSession.id)
    const marketplaceTools = createMarketplaceTools(user?.id || '')

    const tools = { ...artifactTool, ...themeTool, ...identityTool, ...marketplaceTools }

    const stream = streamText({
      model: model,
      system: prompt,
      messages: messages,
      tools,
      stopWhen: stepCountIs(10),
      maxOutputTokens: 16384,
      providerOptions: {
        anthropic: {
          thinking: {
            type: 'enabled',
            budgetTokens: 2048,
          },
        } satisfies AnthropicProviderOptions,
      },
    })

    const reader = stream.toUIMessageStreamResponse()?.body?.getReader()
    if (!reader) {
      console.error('No reader found')
      setIsLoading(false)
      return
    }
    const decoder = new TextDecoder()

    let messageRendered = false
    let buffer = ''

    // Track active tool calls
    const activeToolCalls = new Map<string, { name: string; input: any }>()

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.trim() || line.startsWith(':')) continue

          try {
            // Parse SSE format - strip "data: " prefix if present
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line

            // Skip [DONE] signal
            if (jsonStr === '[DONE]') continue

            const chunk = JSON.parse(jsonStr)

            if (chunk.type === 'text-delta') {
              // Text delta - append directly to content
              assistantMessage.content += chunk.delta
              assistantMessageToDisplay.content += chunk.delta

              if (!messageRendered) {
                await renderMessages([assistantMessageToDisplay])
                messageRendered = true
              } else {
                await updateCurrentMessage(assistantMessageToDisplay)
              }
            } else if (chunk.type === 'tool-input-available') {
              // Tool call
              activeToolCalls.set(chunk.toolCallId, {
                name: chunk.toolName,
                input: chunk.input,
              })

              // Capture tool invocation for structured storage
              toolInvocations.push({
                toolCallId: chunk.toolCallId,
                toolName: chunk.toolName,
                input: chunk.input,
                timestamp: new Date().toISOString(),
              })

              // Add tool call indicator to the message
              const toolDescription = getToolDescription(chunk.toolName, chunk.input)
              const toolIndicator = `\n\n*${toolDescription}...*\n`
              assistantMessageToDisplay.content += toolIndicator

              if (!messageRendered) {
                await renderMessages([assistantMessageToDisplay])
                messageRendered = true
              } else {
                await updateCurrentMessage(assistantMessageToDisplay)
              }
            } else if (chunk.type === 'tool-output-available') {
              // Tool result
              const toolCall = activeToolCalls.get(chunk.toolCallId)

              if (toolCall) {
                // Capture tool output for structured storage
                const invocation = toolInvocations.find(
                  (t) => t.toolCallId === chunk.toolCallId
                )
                if (invocation) {
                  invocation.output = chunk.output
                }

                // Get result indicator and trigger side effects
                const resultIndicator = handleToolResult(toolCall.name, chunk.output, {
                  fetchArtifacts,
                  fetchThemes,
                  fetchIdentities,
                })

                // Append result indicator directly to content
                assistantMessageToDisplay.content += resultIndicator
                await updateCurrentMessage(assistantMessageToDisplay)

                activeToolCalls.delete(chunk.toolCallId)
              }
            } else if (chunk.type === 'finish') {
              // Stream finished
            }
          } catch (error) {
            console.error('Error parsing stream line:', line, error)
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error)
    }

    if (assistantMessage.content === '') {
      assistantMessage.content = '(no response)'
      assistantMessageToDisplay.content = '(no response)'
    }

    // final API call to save messages to honcho (without tool call indicators)
    await saveMessages([userMessage, assistantMessage])

    setIsLoading(false)

    // Attach tool invocations to message metadata if any were captured
    if (toolInvocations.length > 0) {
      assistantMessage.metadata = {
        ...(assistantMessage.metadata ?? {}),
        toolInvocations,
      }
    }

    // Notify working representation widget to refresh after successful authenticated turn
    window.dispatchEvent(new CustomEvent('working-representation-refresh'))
    // Signal tutorial/other listeners that streaming is fully finished
    try {
      window.dispatchEvent(new Event('chat-stream-finished'))
    } catch {}
  }

  const submitToBackendProvider = async (userMessage: ChatMessageWithTimestamp, isRetry = false) => {
    // In guest mode, use guest endpoint (no session persistence)
    if (isGuestMode) {
      await submitGuestMessage(userMessage)
      return
    }

    setIsLoading(true)
    // NOTE: we render and save different versions of the assistant message.
    // if we save the assistant message with tool call indicators, it will confuse the model.
    // so we render the assistant message with tool call indicators, but save the assistant message without tool call indicators.
    const timestamp = new Date()
    const assistantMessage: ChatMessageWithTimestamp = { role: 'assistant', content: '', timestamp }
    const assistantMessageToDisplay: ChatMessageWithTimestamp = { role: 'assistant', content: '', timestamp }

    // Track tool invocations for structured storage
    const toolInvocations: Array<{
      toolCallId: string
      toolName: string
      input: unknown
      output?: unknown
      timestamp: string
    }> = []

    // Track tool_calls in OpenAI format for saving
    const toolCalls: Array<{
      id: string
      type: 'function'
      function: {
        name: string
        arguments: string
      }
    }> = []

    const requestBody: any = {
      messages: [userMessage],
      sessionId: currentSession.id,
      interviewMode: currentSession.interview_mode,
    }

    // Add selected identity ID and name for redundancy
    const selectedIdentityRaw = getSelectedIdentity()
    const selectedIdentity = selectedIdentityRaw + '.json'
    const selectedIdentityId = identities.find((i) => i.name === selectedIdentity)?.id
    if (selectedIdentityId) {
      requestBody.identityId = selectedIdentityId
    }

    // Add weather and location data if available
    // make sure to get local time based on the user's timezone
    requestBody.localTime = new Date().toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    if (weather) {
      requestBody.weather = weather
    }
    if (userLocation) {
      requestBody.location = userLocation
    }
    if (useWebSearch) {
      requestBody.useWebSearch = useWebSearch
    }

    // Include Stripe session ID for grace period verification (if recently upgraded)
    const stripeSessionId = localStorage.getItem('stripe_session_id')
    const stripeSessionTimestamp = localStorage.getItem('stripe_session_timestamp')

    if (stripeSessionId && stripeSessionTimestamp) {
      const age = Date.now() - parseInt(stripeSessionTimestamp, 10)

      if (age < 180000) { // Less than 3 minutes old
        requestBody.stripeSessionId = stripeSessionId
      } else {
        // Session is stale, clear it
        localStorage.removeItem('stripe_session_id')
        localStorage.removeItem('stripe_session_timestamp')
      }
    }

    // Use fetch directly instead of Eden Treaty for streaming
    // Get access token from localStorage for authorization
    // Use fetchWithAuth to automatically handle token refresh on 401
    let response: Response
    try {
      response = await fetchWithAuth(`${API_URL}/api/chat/turn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })
    } catch (error) {
      console.error('Chat request failed:', error)
      setIsLoading(false)
      // fetchWithAuth triggers redirect to /auth/login on auth failure
      return
    }

    // Clear session ID after first successful request (grace period used)
    if (response.ok && stripeSessionId) {
      localStorage.removeItem('stripe_session_id')
      localStorage.removeItem('stripe_session_timestamp')
    }

    if (!response.ok || !response.body) {
      // Handle rate limit error from backend
      if (response.status === 429) {
        try {
          const errorData = await response.json()
          if (errorData.resetTime && onUsageLimitReached) {
            onUsageLimitReached(errorData.resetTime)
          }
          assistantMessage.content =
            "You've reached your daily message limit. Please [upgrade](#upgrade) to continue."
          assistantMessageToDisplay.content = assistantMessage.content
          assistantMessage.onUpgradeClick = onOpenSubscription
          await renderMessages([assistantMessageToDisplay])
        } catch (e) {
          console.error('Error parsing rate limit response:', e)
        }
      }
      setIsLoading(false)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let messageRendered = false
    let buffer = ''

    // Track active tool calls and content blocks
    const activeToolCalls = new Map<string, { name: string; input: any; accumulatedInput: string }>()
    const contentBlockIndexMap = new Map<number, string>() // Maps content block index to tool call ID

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.trim() || line.startsWith(':')) continue

          try {
            // Parse SSE format - strip "data: " prefix if present
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line

            const event = JSON.parse(jsonStr)

            // Handle OpenAI-compatible chunks (from xAI, OpenAI, etc.)
            if (event.object === 'chat.completion.chunk' && event.choices?.[0]) {
              const choice = event.choices[0]
              
              // Handle text content
              if (choice.delta?.content) {
                assistantMessage.content += choice.delta.content
                assistantMessageToDisplay.content += choice.delta.content

                if (!messageRendered) {
                  await renderMessages([assistantMessageToDisplay])
                  messageRendered = true
                } else {
                  await updateCurrentMessage(assistantMessageToDisplay)
                }
              }

              // Handle tool calls (OpenAI streams tool calls incrementally)
              if (choice.delta?.tool_calls) {
                for (const toolCallDelta of choice.delta.tool_calls) {
                  const index = toolCallDelta.index ?? 0
                  const toolCallId = toolCallDelta.id
                  
                  if (toolCallId) {
                    if (!activeToolCalls.has(toolCallId)) {
                      // New tool call starting
                      activeToolCalls.set(toolCallId, {
                        name: toolCallDelta.function?.name || '',
                        input: {},
                        accumulatedInput: toolCallDelta.function?.arguments || '',
                      })
                    } else {
                      // Accumulate arguments for existing tool call
                      const toolCall = activeToolCalls.get(toolCallId)!
                      toolCall.accumulatedInput += toolCallDelta.function?.arguments || ''
                      if (toolCallDelta.function?.name) {
                        toolCall.name = toolCallDelta.function.name
                      }
                    }
                  }
                }
              }

              // Handle finish reason - if tool_calls, wait for tool execution
              if (choice.finish_reason === 'tool_calls') {
                // Parse all accumulated tool calls and display them (only once per tool call)
                for (const [toolCallId, toolCall] of activeToolCalls.entries()) {
                  // Skip if already processed (check if toolCallId already in toolInvocations)
                  if (toolInvocations.some(t => t.toolCallId === toolCallId)) {
                    continue
                  }
                  
                  if (toolCall.accumulatedInput) {
                    try {
                      toolCall.input = JSON.parse(toolCall.accumulatedInput)

                      const toolDescription = getToolDescription(toolCall.name, toolCall.input)
                      const toolIndicator = `\n\n*${toolDescription}...*\n`
                      assistantMessageToDisplay.content += toolIndicator

                      if (!messageRendered) {
                        await renderMessages([assistantMessageToDisplay])
                        messageRendered = true
                      } else {
                        await updateCurrentMessage(assistantMessageToDisplay)
                      }

                      // Capture tool invocation for structured storage
                      toolInvocations.push({
                        toolCallId: toolCallId,
                        toolName: toolCall.name,
                        input: toolCall.input,
                        timestamp: new Date().toISOString(),
                      })

                      // Store tool call in OpenAI format
                      toolCalls.push({
                        id: toolCallId,
                        type: 'function',
                        function: {
                          name: toolCall.name,
                          arguments: toolCall.accumulatedInput,
                        },
                      })
                    } catch (e) {
                      console.error('Error parsing tool input JSON:', e)
                    }
                  }
                }
              } else if (choice.finish_reason && choice.finish_reason !== 'tool_calls') {
                // Stream finished (stop, length, etc.)
                // Don't break here - wait for 'finish' event from backend
              }
            }
            // Handle Anthropic streaming events
            else if (event.type === 'message_start') {
              // Message starting - render empty assistant message
              if (!messageRendered) {
                await renderMessages([assistantMessageToDisplay])
                messageRendered = true
              }
            } else if (event.type === 'content_block_start') {
              const block = event.content_block

              if (block.type === 'text') {
                // Text block starting - make sure message is rendered
                if (!messageRendered) {
                  await renderMessages([assistantMessageToDisplay])
                  messageRendered = true
                }
              } else if (block.type === 'tool_use') {
                // Tool use block starting - store it but don't display yet (input is streaming)
                const toolCallId = block.id
                const toolName = block.name

                activeToolCalls.set(toolCallId, {
                  name: toolName,
                  input: {},
                  accumulatedInput: '',
                })

                contentBlockIndexMap.set(event.index, toolCallId)
              }
            } else if (event.type === 'content_block_delta') {
              const delta = event.delta

              if (delta.type === 'text_delta') {
                // Text delta - append to content
                assistantMessage.content += delta.text
                assistantMessageToDisplay.content += delta.text

                if (!messageRendered) {
                  await renderMessages([assistantMessageToDisplay])
                  messageRendered = true
                } else {
                  await updateCurrentMessage(assistantMessageToDisplay)
                }
              } else if (delta.type === 'input_json_delta') {
                // Tool input being streamed
                const toolCallId = contentBlockIndexMap.get(event.index)
                if (toolCallId) {
                  const toolCall = activeToolCalls.get(toolCallId)
                  if (toolCall) {
                    toolCall.accumulatedInput += delta.partial_json
                  }
                }
              }
            } else if (event.type === 'content_block_stop') {
              // Content block finished
              const toolCallId = contentBlockIndexMap.get(event.index)
              if (toolCallId) {
                const toolCall = activeToolCalls.get(toolCallId)
                if (toolCall && toolCall.accumulatedInput) {
                  try {
                    toolCall.input = JSON.parse(toolCall.accumulatedInput)

                    const toolDescription = getToolDescription(toolCall.name, toolCall.input)
                    const toolIndicator = `\n\n*${toolDescription}...*\n`
                    assistantMessageToDisplay.content += toolIndicator

                    if (!messageRendered) {
                      await renderMessages([assistantMessageToDisplay])
                      messageRendered = true
                    } else {
                      await updateCurrentMessage(assistantMessageToDisplay)
                    }

                    // Capture tool invocation for structured storage
                    toolInvocations.push({
                      toolCallId: toolCallId,
                      toolName: toolCall.name,
                      input: toolCall.input,
                      timestamp: new Date().toISOString(),
                    })
                  } catch (e) {
                    console.error('Error parsing tool input JSON:', e)
                  }
                }
              }
            } else if (event.type === 'tool_result_available') {
              // Custom event from backend with tool execution result
              const toolCall = activeToolCalls.get(event.toolCallId)

              if (toolCall) {
                // Capture tool output for structured storage
                const invocation = toolInvocations.find(t => t.toolCallId === event.toolCallId)
                if (invocation) {
                  invocation.output = event.output
                }

                // Get result indicator and trigger side effects
                const resultIndicator = handleToolResult(event.toolName, event.output, {
                  fetchArtifacts,
                  fetchThemes,
                  fetchIdentities,
                })

                // Append result indicator to content
                assistantMessageToDisplay.content += resultIndicator
                await updateCurrentMessage(assistantMessageToDisplay)

                activeToolCalls.delete(event.toolCallId)
              }
            } else if (event.type === 'message_delta') {
              // Message delta (usage info, etc) - ignore
            } else if (event.type === 'message_stop') {
              // Message finished - if tools were called, backend will continue
            } else if (event.type === 'finish') {
              // Custom finish event from our backend
              break
            } else if (event.type === 'error') {
              console.error('Stream error:', event.error)
              break
            }
          } catch (error) {
            console.error('Error parsing stream line:', line, error)
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error)
    }

    if (assistantMessage.content === '') {
      // If this is not a retry, immediately retry with the same inputs
      if (!isRetry) {
        await submitToBackendProvider(userMessage, true)
        return
      }
      // If this is already a retry, set the no response message
      assistantMessage.content = '(no response)'
      assistantMessageToDisplay.content = '(no response)'
    }

    // Prepare messages to save: assistant message with tool_calls, then tool result messages
    const messagesToSave: ChatMessageWithTimestamp[] = [userMessage]

    // If there were tool calls, save assistant message with tool_calls in metadata
    if (toolCalls.length > 0) {
      assistantMessage.metadata = {
        ...(assistantMessage.metadata ?? {}),
        tool_calls: toolCalls,
      }
      messagesToSave.push(assistantMessage)

      // Save each tool result as a separate message
      for (const invocation of toolInvocations) {
        if (invocation.output !== undefined) {
          const toolResultMessage: ChatMessageWithTimestamp = {
            role: 'tool',
            content: JSON.stringify(invocation.output),
            timestamp: new Date(invocation.timestamp),
            tool_call_id: invocation.toolCallId,
            metadata: {
              messageType: 'tool',
              tool_call_id: invocation.toolCallId,
              tool_name: invocation.toolName,
              // Output is stored in content, not duplicated in metadata
            },
          }
          messagesToSave.push(toolResultMessage)
        }
      }
    } else {
      // No tool calls, just save the assistant message as-is
      messagesToSave.push(assistantMessage)
    }

    // final API call to save messages to honcho
    await saveMessages(messagesToSave)

    setIsLoading(false)

    // Notify working representation widget to refresh after successful authenticated turn
    window.dispatchEvent(new CustomEvent('working-representation-refresh'))
    // Signal tutorial/other listeners that streaming is fully finished
    try {
      window.dispatchEvent(new Event('chat-stream-finished'))
    } catch {}
  }

  const handleAuthStepComplete = async (
    nextStep: AuthStep,
    credentials?: Partial<AuthCredentials>
  ) => {
    // Store credentials as user progresses through steps
    if (credentials?.email) {
      setAuthEmail(credentials.email)
    }
    if (credentials?.password) {
      setAuthPassword(credentials.password)
    }

    // Show assistant message for the next step (not persisted to localStorage)
    let assistantMessage = ''
    if (nextStep === 'password') {
      assistantMessage = 'Great! Now choose a password (minimum 8 characters).'
    } else if (nextStep === 'confirm-password') {
      assistantMessage =
        "Please confirm your password to make sure it's correct."
    }

    if (assistantMessage) {
      const message: ChatMessageWithTimestamp = {
        role: 'assistant',
        content: assistantMessage,
        timestamp: new Date(),
      }
      await renderMessages([message])
    }

    setAuthStep(nextStep)
  }

  const handleAuthSubmit = async (credentials: AuthCredentials) => {
    try {
      // Call auth API
      const formData = new FormData()
      formData.set('email', credentials.email)
      formData.set('password', credentials.password)

      const result = await authenticate(formData, 'signup')

      if (result.error) {
        // Handle specific errors
        if (
          result.error.includes('already exists') ||
          result.error.includes('already registered')
        ) {
          // User has existing account - close in-chat flow and direct to nav login
          setShowAuthFlow(false)
          setAuthStep(null)
          const switchMessage: ChatMessageWithTimestamp = {
            role: 'assistant',
            content:
              'It looks like you already have an account! Please use the login button in the top navigation bar.',
            timestamp: new Date(),
          }
          await renderMessages([switchMessage])
          return
        }

        throw new Error(result.error)
      }

      // Authentication successful
      // Note: Login should never reach here since it's handled via modal
      // This is only for signup flow

      // Signup flow - show email verification prompt (not persisted to localStorage)
      setAuthStep('check-email')
      const checkEmailMessage: ChatMessageWithTimestamp = {
        role: 'assistant',
        content: `Perfect! I've sent a confirmation email to ${credentials.email}. Please check your inbox and click the verification link.`,
        timestamp: new Date(),
      }
      await renderMessages([checkEmailMessage])

      // Poll for email verification
      await pollForEmailVerification()

      // Once verified, migrate guest session
      await migrateGuestSessionToBackend()

      setAuthStep('complete')
      setShowAuthFlow(false)
    } catch (error) {
      console.error('Auth error:', error)
      const errorMessage: ChatMessageWithTimestamp = {
        role: 'assistant',
        content: `Hmm, there was an issue: ${error instanceof Error ? error.message : 'Unknown error'}. Let's try again.`,
        timestamp: new Date(),
      }
      await renderMessages([errorMessage])
    }
  }

  const migrateGuestSessionToBackend = async () => {
    const guestMessages = getGuestMessages()

    if (!guestMessages.length) {
      return
    }

    try {
      // Extract just the message content (remove timestamps for API)
      const messages = guestMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }))

      await migrateGuestSession(messages)

      // Clear guest session from localStorage
      clearGuestSession()

      // Show success message
      const successMessage: ChatMessageWithTimestamp = {
        role: 'assistant',
        content: "Great! Your account is all set up. Let's keep going!",
        timestamp: new Date(),
      }
      await renderMessages([successMessage])
    } catch (error) {
      console.error('Failed to migrate guest session:', error)
      // TODO sentry
    }
  }

  const pollForEmailVerification = async (
    maxAttempts = 60,
    intervalMs = 5000
  ) => {
    // Poll backend to check if user verified email
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs))

      try {
        const response = await fetchWithAuth(
          `${API_URL}/auth/check-verification`,
          {
            method: 'GET',
            credentials: 'include',
          }
        )

        if (response.ok) {
          const data = await response.json()
          if (data.verified) {
            // Show success message (not persisted to localStorage)
            const successMessage: ChatMessageWithTimestamp = {
              role: 'assistant',
              content: 'Email verified! Setting up your account...',
              timestamp: new Date(),
            }
            await renderMessages([successMessage])
            return // Success
          }
        }
      } catch (error) {
        console.error('Verification check error:', error)
      }
    }

    // Timeout - show resend option (not persisted to localStorage)
    const timeoutMessage: ChatMessageWithTimestamp = {
      role: 'assistant',
      content:
        'Still waiting for email verification. Check your spam folder? I can resend the email if needed.',
      timestamp: new Date(),
    }
    await renderMessages([timeoutMessage])

    throw new Error('Email verification timeout')
  }

  const submitGuestMessage = async (userMessage: ChatMessageWithTimestamp) => {
    setIsLoading(true)

    const assistantMessage: ChatMessageWithTimestamp = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    }

    // Build request body with full conversation history from localStorage
    const guestMessages = getGuestMessages()
    const requestBody: any = {
      messages: [...guestMessages, userMessage],
      localTime: new Date().toLocaleString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    }

    // Add weather/location if available
    if (weather) {
      requestBody.weather = weather
    }
    if (userLocation) {
      requestBody.location = userLocation
    }

    // Call backend STREAMING endpoint WITHOUT auth
    // Backend uses IP-based rate limiting
    let response: Response
    try {
      response = await fetch(`${API_URL}/api/chat/guest-turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })
    } catch (error) {
      console.error('Guest chat request failed:', error)
      setIsLoading(false)
      return
    }

    if (!response.ok || !response.body) {
      // Handle rate limit error from backend
      if (response.status === 429) {
        try {
          const errorData = await response.json()
          if (errorData.resetTime && onUsageLimitReached) {
            onUsageLimitReached(errorData.resetTime)
          }
        } catch (e) {
          console.error('Error parsing rate limit response:', e)
        }
        assistantMessage.content =
          "You've reached the guest message limit. Please create an account to continue."
        await renderMessages([assistantMessage])
        setShowAuthFlow(true)
        setAuthStep('email')
      }
      setIsLoading(false)
      return
    }

    // Stream response same as authenticated flow
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    assistantMessage.content = ''
    let messageRendered = false
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.trim() || line.startsWith(':')) continue

          try {
            // Parse SSE format - strip "data: " prefix if present
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line

            const event = JSON.parse(jsonStr)

            // Handle OpenAI-compatible chunks (from xAI, OpenAI, etc.)
            if (event.object === 'chat.completion.chunk' && event.choices?.[0]) {
              const choice = event.choices[0]
              
              // Handle text content
              if (choice.delta?.content) {
                assistantMessage.content += choice.delta.content

                if (!messageRendered) {
                  await renderMessages([assistantMessage])
                  messageRendered = true
                } else {
                  await updateCurrentMessage(assistantMessage)
                }
              }

              if (choice.finish_reason && choice.finish_reason !== 'tool_calls') {
                // Stream finished (stop, length, etc.)
                // Don't break here - wait for 'finish' event from backend
              }
            }
            // Handle Anthropic streaming events
            else if (event.type === 'message_start') {
              // Message starting - render empty assistant message
              if (!messageRendered) {
                await renderMessages([assistantMessage])
                messageRendered = true
              }
            } else if (event.type === 'content_block_start') {
              const block = event.content_block

              if (block.type === 'text') {
                // Text block starting - make sure message is rendered
                if (!messageRendered) {
                  await renderMessages([assistantMessage])
                  messageRendered = true
                }
              }
            } else if (event.type === 'content_block_delta') {
              const delta = event.delta

              if (delta.type === 'text_delta') {
                // Text delta - append to content
                assistantMessage.content += delta.text

                if (!messageRendered) {
                  await renderMessages([assistantMessage])
                  messageRendered = true
                } else {
                  await updateCurrentMessage(assistantMessage)
                }
              }
            } else if (event.type === 'finish') {
              // Custom finish event from our backend
              break
            } else if (event.type === 'error') {
              console.error('Stream error:', event.error)
              break
            }
          } catch (error) {
            console.error('Error parsing stream line:', line, error)
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error)
    }

    setIsLoading(false)

    // Add assistant message to localStorage (no backend persistence)
    addGuestMessage(assistantMessage)
    // Signal tutorial/other listeners that streaming is fully finished
    try {
      window.dispatchEvent(new Event('chat-stream-finished'))
    } catch {}
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <ChatHeader
        availableProviders={availableProviders}
        selectedProvider={selectedProvider}
        selectedModel={selectedModel}
        availableModels={availableModels}
        isLoadingModels={isLoadingModels}
        modelError={modelError}
        onProviderChange={onProviderChange}
        onModelChange={onModelChange}
        currentSessionId={currentSession.id}
      />

      <MessagesDisplay
        messages={currentSession.messages}
        isLoadingMessages={isLoadingMessages}
        fontMode={fontMode}
        textSize={textSize}
        targetMessageId={targetMessageId}
        onTargetMessageScrolled={() => setTargetMessageId(undefined)}
        hasMoreMessages={hasMoreMessages}
        isLoadingMoreMessages={isLoadingMoreMessages}
        onLoadMore={loadMoreMessages}
        hasMorePreviousMessages={hasMorePreviousMessages}
        isLoadingPreviousMessages={isLoadingPreviousMessages}
        onLoadPrevious={loadPreviousMessages}
        onReplyToMessage={handleReplyToMessage}
        replyingToMessageId={replyingTo?.id}
        currentSessionId={currentSession.id}
      />

      {showAuthFlow && authStep ? (
        <InChatAuthFlow
          step={authStep}
          onStepComplete={handleAuthStepComplete}
          onSubmit={handleAuthSubmit}
          storedEmail={authEmail}
          storedPassword={authPassword}
        />
      ) : (
        <MessageInput
          initialValue={initialInputValue}
          isLoading={isLoading || showAuthFlow}
          isLoadingMessages={isLoadingMessages}
          selectedProvider={selectedProvider}
          selectedModel={selectedModel}
          isLoadingModels={isLoadingModels}
          onSubmit={handleSubmit}
          onWebSearchToggle={handleWebSearchToggle}
          onDisplayMessage={handleDisplayMessage}
          replyingTo={replyingTo}
          onClearReply={handleClearReply}
          disabled={showAuthFlow}
          identityName={selectedIdentityName}
        />
      )}
    </div>
  )
}
